import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { po, backendUrl } from "./api";
import { ImagePlus, Loader2, MessageCircle, Eye } from "lucide-react";

export function ProofLink({ token, order }) {
  if (!order.payment_proof) return null;
  const view = async () => {
    const w = window.open("", "_blank");
    const r = await fetch(`${backendUrl}/api/pickup-orders/proof/view`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, id: order.id }) });
    if (!r.ok) { w?.close(); return toast.error("Could not open screenshot"); }
    const url = URL.createObjectURL(await r.blob());
    if (w) w.location.href = url; else window.location.href = url;
  };
  return (
    <button type="button" onClick={view} className="inline-flex items-center gap-2 text-base font-semibold text-emerald-800 underline" data-testid="po-proof-view">
      <Eye className="h-4 w-4" />View payment screenshot
    </button>
  );
}

export function ReadyAlert({ token, order }) {
  const go = async () => {
    try { const r = await po("ready-message", token, { id: order.id }); window.open(r.wa_url, "_blank"); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Button className="h-12 bg-[#128C7E] text-base hover:bg-[#0e6f63]" onClick={go} data-testid="po-ready-alert">
      <MessageCircle className="mr-2 h-4 w-4" />Send "Your order is ready" on WhatsApp
    </Button>
  );
}

export function VerifyPayment({ token, order, onDone }) {
  const [v, setV] = useState({ method: "UPI", reference: "", amount: order.total });
  const [up, setUp] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("token", token); fd.append("id", order.id); fd.append("file", file);
      const r = await fetch(`${backendUrl}/api/pickup-orders/proof/upload`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Upload failed");
      toast.success("Payment screenshot attached"); onDone(d.order);
    } catch (e) { toast.error(e.message); } finally { setUp(false); }
  };
  const go = async () => {
    try { onDone((await po("verify-payment", token, { id: order.id, ...v, amount: Number(v.amount) })).order); toast.success("Payment verified — order is PAID"); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3 rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 p-4" data-testid="po-verify-box">
      <p className="font-semibold text-emerald-900">Verify payment — check your PhonePe / bank app first</p>
      <label className="flex cursor-pointer flex-wrap items-center gap-2 rounded-xl border-2 border-dashed border-emerald-400 bg-white p-3 text-base" data-testid="po-proof-label">
        {up ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5 text-emerald-700" />}
        {order.payment_proof ? `Screenshot attached (${order.payment_proof.name}) — tap to replace` : "Attach customer's payment screenshot (optional)"}
        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="po-proof-input" />
      </label>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1"><Label>Method</Label>
          <select className="h-11 w-full rounded-md border bg-white px-2 text-base" value={v.method} onChange={(e) => setV({ ...v, method: e.target.value })} data-testid="po-verify-method">
            {["UPI", "CASH", "CARD", "BANK", "OTHER"].map((m) => <option key={m}>{m}</option>)}</select></div>
        <div className="space-y-1"><Label>UPI / transaction ref</Label><Input className="h-11 text-base" value={v.reference} onChange={(e) => setV({ ...v, reference: e.target.value })} data-testid="po-verify-ref" /></div>
        <div className="space-y-1"><Label>Amount received</Label><Input className="h-11 text-base" type="number" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} data-testid="po-verify-amount" /></div>
      </div>
      <Button className="h-11 bg-emerald-700 hover:bg-emerald-800" onClick={go} data-testid="po-verify-submit">Mark payment verified</Button>
    </div>
  );
}

export function PetpoojaBill({ token, order, onDone }) {
  const [bill, setBill] = useState(order.petpooja_bill_no || "");
  const go = async () => {
    try { onDone((await po("petpooja-bill", token, { id: order.id, bill_no: bill })).order); toast.success("Petpooja bill linked"); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-2xl border-2 border-[#f1dca6] bg-[#fffbf2] p-4" data-testid="po-bill-box">
      <div className="flex-1 space-y-1"><Label>Petpooja bill number (bill once in Petpooja, then enter it here)</Label>
        <Input className="h-11 text-base" value={bill} onChange={(e) => setBill(e.target.value)} disabled={!!order.petpooja_bill_no} data-testid="po-bill-input" /></div>
      {!order.petpooja_bill_no && <Button className="h-11" onClick={go} data-testid="po-bill-save">Save bill no.</Button>}
    </div>
  );
}
